import ky, { HTTPError } from "ky";
import type {
  ApiResponse,
  ApiSuccessResponse,
  ApiErrorResponse,
} from "~shared/api-response";

// Type guard for success
export function isApiSuccess<T>(
  res: ApiResponse<T>,
): res is ApiSuccessResponse<T> {
  return res.success === true;
}

// Type guard for error
export function isApiError<T>(res: ApiResponse<T>): res is ApiErrorResponse {
  return res.success === false;
}

// Helper to fetch and parse API responses using ky
// Now throws errors for non-success responses, eliminating if conditions onSuccess
export async function fetchApi<T>(
  input: string | URL,
  options?: Parameters<typeof ky>[1],
): Promise<T> {
  try {
    const response = await ky(input, options).json<ApiResponse<T>>();

    if (isApiSuccess(response)) {
      return response.data;
    }

    // Create a regular Error object that can be handled by onError
    throw new Error(response.message || "API request failed");
  } catch (error) {
    // Handle ky HTTPError to extract response body
    if (error instanceof HTTPError) {
      // Check if response body is still readable
      if (error.response && !error.response.bodyUsed) {
        const errorData = (await error.response.json()) as ApiErrorResponse;
        // Throw the detailed error message directly - no try/catch needed
        throw new Error(errorData.message || "API request failed");
      } else {
        // Response body already consumed, use original error message
        throw new Error(error.message || "API request failed");
      }
    }

    // Re-throw other errors as-is
    throw error;
  }
}
